window.utilities = window.utilities || {};
window.utilities.cardHelpers = window.utilities.cardHelpers || {};

window.utilities.cardHelpers.extractCompanionProductsJson = (product) => {
  if (!product || typeof product !== 'object') return null;

  const fromReference = product.companionProducts?.reference?.field?.value;
  if (fromReference) {
    return fromReference;
  }

  if (product.companionProducts?.value) {
    return product.companionProducts.value;
  }

  const metafields = Array.isArray(product.metafields) ? product.metafields : [];
  const entry = metafields.find((m) => m?.key === 'companionProducts');
  return entry?.value ?? null;
};

window.utilities.cardHelpers.buildSwatchesFromCompanionProducts = (companionJson, productId) => {
  if (!companionJson) return [];

  let parsed = companionJson;
  if (typeof companionJson === 'string') {
    parsed = window.utilities.safeParseJSON(companionJson, null);
  }

  if (parsed && typeof parsed === 'object' && parsed.product_json?.value) {
    parsed = parsed.product_json.value;
  }

  if (!Array.isArray(parsed)) {
    return [];
  }

  const localeCode = window.localeCode || `${window.locale || 'en'}-${window.Shopify?.country || 'US'}`;
  const shortLocale = window.locale || 'en';
  const numericId = window.utilities.extractIdFromGid
    ? window.utilities.extractIdFromGid(productId)
    : String(productId || '').replace(/\D/g, '');
  const fullProductGid = String(productId || '').startsWith('gid://')
    ? String(productId)
    : `gid://shopify/Product/${numericId}`;

  const swatches = parsed
    .map((item) => {
      const color = item.color || {};
      const label =
        color[localeCode] ||
        color[shortLocale] ||
        color['en-US'] ||
        color['en-CA'] ||
        color['en'] ||
        color['fr-CA'] ||
        color['fr'] ||
        Object.values(color)[0] ||
        '';
      const swatchProductId = item.productID || '';
      const swatchNumericId = window.utilities.extractIdFromGid
        ? window.utilities.extractIdFromGid(swatchProductId)
        : String(swatchProductId).replace(/\D/g, '');
      const isChecked = swatchProductId === fullProductGid || (numericId.length > 0 && swatchNumericId === numericId);

      return {
        productID: swatchProductId,
        image: item.swatch_image || '',
        label,
        isChecked,
      };
    })
    .filter((swatch) => swatch.productID && swatch.image);

  const checkedIndex = swatches.findIndex((swatch) => swatch.isChecked);
  if (checkedIndex > 0) {
    const [checked] = swatches.splice(checkedIndex, 1);
    swatches.unshift(checked);
  }

  return swatches;
};

let bloomreachTranslationsCache = null;

const getBloomreachTranslations = () => {
  if (bloomreachTranslationsCache) return bloomreachTranslationsCache;
  const island = document.getElementById('bloomreach-i18n');
  try {
    bloomreachTranslationsCache = island?.textContent ? JSON.parse(island.textContent) : {};
  } catch (ex) {
    bloomreachTranslationsCache = {};
  }
  return bloomreachTranslationsCache;
};

window.utilities.cardHelpers.getSwatchOverflowLabel = (remaining) => {
  const moreOptions = getBloomreachTranslations().product_options?.swatch_more_options || {};
  const template = remaining === 1 ? moreOptions.one : moreOptions.other;
  return template.replace(/\{\{\s*count\s*\}\}/, remaining);
};

const parseMetafieldBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  return value === 'true';
};

const BADGE_FLAG_METAFIELD_MAP = [
  ['bestseller_us', 'bestsellerUS'],
  ['bestseller_ca', 'bestsellerCA'],
  ['trending_us', 'trendingUS'],
  ['trending_ca', 'trendingCA'],
  ['isNewArrivalUS', 'isNewArrivalUS'],
  ['isNewArrivalCA', 'isNewArrivalCA'],
  ['sellingFastUS', 'sellingFastUS'],
  ['sellingFastCA', 'sellingFastCA'],
];

const createMetafieldAccessors = (metafields) => {
  const entries = Array.isArray(metafields) ? metafields.filter(Boolean) : [];

  return {
    getMetafieldValue: (key) => {
      const entry = entries.find((m) => m?.key === key);
      return entry?.value ?? null;
    },
    hasMetafield: (key) => entries.some((m) => m?.key === key),
  };
};

// Only includes badge flags when Storefront returned the metafield (avoids overwriting Bloomreach signals with false).
window.utilities.cardHelpers.buildBadgeFlagsFromMetafields = (getMetafieldValue, hasMetafield) => {
  const flags = {};

  for (const [metafieldKey, flagKey] of BADGE_FLAG_METAFIELD_MAP) {
    if (hasMetafield(metafieldKey)) {
      flags[flagKey] = parseMetafieldBoolean(getMetafieldValue(metafieldKey));
    }
  }

  const socialConfidenceKey = `socialConfidence${window.Shopify?.country || 'US'}`;
  if (hasMetafield(socialConfidenceKey)) {
    flags.socialConfidence = getMetafieldValue(socialConfidenceKey);
  }

  return flags;
};

window.utilities.cardHelpers.transformProductBadgeMetadataToCardData = (product) => {
  if (!product || typeof product !== 'object') return null;

  const { getMetafieldValue, hasMetafield } = createMetafieldAccessors(product.metafields);
  const badgeData = {
    id: product.id,
    flags: window.utilities.cardHelpers.buildBadgeFlagsFromMetafields(getMetafieldValue, hasMetafield),
  };

  if (hasMetafield('collaboration')) {
    badgeData.logoBadge = getMetafieldValue('collaboration');
  }

  return badgeData;
};

window.utilities.cardHelpers.transformProductMetadataToCardData = (product) => {
  if (!product || typeof product !== 'object') return null;

  const { getMetafieldValue, hasMetafield } = createMetafieldAccessors(product.metafields);

  const highlightsRaw = getMetafieldValue('highlights');
  const logoBadge = getMetafieldValue('collaboration');

  const highlights =
    typeof highlightsRaw === 'string' ? highlightsRaw.split(',').map((label) => ({ label: label.trim() })) : [];

  const defaultDimensions = { width: 1000, height: 1000 };
  const featuredImageUrl = product.featuredImage?.url || '';
  const featuredImage = featuredImageUrl ? { src: featuredImageUrl, width: 1000, height: 1000 } : null;

  let image = featuredImage;
  let imageFallback = null;

  const mediaGalleryRaw = getMetafieldValue('media_gallery_assets');
  const mediaGallery = (() => {
    if (!mediaGalleryRaw) return [];
    const parsed =
      typeof mediaGalleryRaw === 'string' ? window.utilities.safeParseJSON(mediaGalleryRaw) : mediaGalleryRaw;
    return Array.isArray(parsed) ? parsed : [];
  })();

  if (mediaGallery.length > 0) {
    const contextualOrder = ['campaign-full', 'look-full', 'campaign-part', 'look-part'];
    const imageOrder = [
      'main',
      'spotlight',
      'contextual',
      'alt1',
      'alt2',
      'video',
      'alt3',
      'alt4',
      'alt5',
      'alt6',
      'contextual',
    ];

    const findByType = (type) => mediaGallery.find((e) => e?.imageType === type && e?.imageUrl);
    const getContextual = () => contextualOrder.map(findByType).find(Boolean);

    const orderedEntries = [];
    for (const order of imageOrder) {
      if (order === 'contextual') {
        const ctx = getContextual();
        if (ctx) orderedEntries.push(ctx);
      } else {
        const entry = findByType(order);
        if (entry) orderedEntries.push(entry);
      }
    }

    const videoEntry = findByType('video');
    const firstImage = orderedEntries.find((e) => e?.imageType !== 'video');

    if (videoEntry?.imageUrl) {
      image = { src: videoEntry.imageUrl, ...defaultDimensions };
      imageFallback = firstImage?.imageUrl ? { src: firstImage.imageUrl, ...defaultDimensions } : featuredImage;
    } else if (firstImage?.imageUrl) {
      image = { src: firstImage.imageUrl, ...defaultDimensions };
    }
  }

  if (!image) {
    image = { src: '', width: 1000, height: 1000 };
  }

  const customMediaGalleryAssets = (() => {
    if (typeof mediaGalleryRaw === 'string' && mediaGalleryRaw.trim()) {
      return mediaGalleryRaw;
    }

    if (mediaGallery.length > 0) {
      return JSON.stringify(mediaGallery);
    }

    return null;
  })();

  const price = product.priceRange?.maxVariantPrice?.amount
    ? `$${parseFloat(product.priceRange.maxVariantPrice.amount)}`
    : null;

  const compareAtPrice =
    product.compareAtPriceRange?.maxVariantPrice?.amount &&
    parseFloat(product.compareAtPriceRange.maxVariantPrice.amount) !== 0
      ? `$${parseFloat(product.compareAtPriceRange.maxVariantPrice.amount)}`
      : null;

  const flags = {
    publishDateUS: getMetafieldValue('publish_date_us'),
    publishDateCA: getMetafieldValue('publish_date_ca'),
    nonReturnableType: getMetafieldValue('non_returnable_type'),
    silhouette: getMetafieldValue('silhouette'),
    heelType: getMetafieldValue('heelType'),
    promotionalMessageUSEN: getMetafieldValue('promotional_message_us_en'),
    promotionalMessageCAEN: getMetafieldValue('promotional_message_ca_en'),
    promotionalMessageCAFR: getMetafieldValue('promotional_message_ca_fr'),
    promotionalPriceMessageUSEN: getMetafieldValue('promo_applied_message_us_en'),
    promotionalPriceMessageCAEN: getMetafieldValue('promo_applied_message_ca_en'),
    promotionalPriceMessageCAFR: getMetafieldValue('promo_applied_message_ca_fr'),
    onlineExclusive: getMetafieldValue('onlineExclusive') === 'true',
    comingSoonUS: getMetafieldValue('comingSoonUS'),
    comingSoonCA: getMetafieldValue('comingSoonCA'),
    preOrderUS: getMetafieldValue('preOrderUS'),
    preOrderCA: getMetafieldValue('preOrderCA'),
    ...window.utilities.cardHelpers.buildBadgeFlagsFromMetafields(getMetafieldValue, hasMetafield),
  };

  const formatted = {
    id: product.id,
    title: product.title,
    productType: product.productType,
    color: getMetafieldValue('color'),
    gender: getMetafieldValue('gender'),
    category: getMetafieldValue('category'),
    silhouette: getMetafieldValue('silhouette'),
    url: window.Shopify.routes.root + `products/${product.handle}`,
    availableForSale: product.availableForSale,
    totalInventory: product.totalInventory,
    tags: product.tags || [],
    price,
    compareAtPrice,
    image,
    imageFallback: imageFallback || undefined,
    customMediaGalleryAssets,
    highlights,
    flags,
    logoBadge,
    swatches: window.utilities.cardHelpers.buildSwatchesFromCompanionProducts(
      window.utilities.cardHelpers.extractCompanionProductsJson(product),
      product.id,
    ),
  };

  return formatted;
};

window.utilities.cardHelpers.isLowStock = (data) => {
  const lowStockThreshold = window.product.lowStockValue;

  if (typeof data.totalInventory === 'number') {
    return data.totalInventory <= lowStockThreshold;
  }

  return (
    data.flags[`inventoryStock${window.Shopify.country}`] &&
    data.flags[`inventoryStock${window.Shopify.country}`].toLowerCase() === 'lowstock'
  );
};

window.utilities.cardHelpers.isInStock = (data) => {
  if (typeof data.totalInventory === 'number') {
    return data.totalInventory > 0;
  }

  return (
    data.flags[`inventoryStock${window.Shopify.country}`] &&
    data.flags[`inventoryStock${window.Shopify.country}`].toLowerCase() !== 'outofstock'
  );
};
